import cloneDeep from 'lodash/cloneDeep'
import isEmpty from 'lodash/isEmpty'
import { createSelector } from 'reselect'
import { isV4Routed } from 'lib/api/ssapi/secondStepApi.router'
import { adaptSitesV4ToLegacySites } from 'lib/api/adapters/sites'
import ssapiSelectors from 'store/ssapiManager/selectors'

/**
 * @param {array} activeDigitalLicenses - Collection of active licenses (mapped to v2 shape)
 * @param {object} userAccess - User access object
 * @param {string} filterType - 'K5P' | 'MSP'
 * @returns {{
 *  ...license,
 *  grades: string[] // A list of grades the user has access to for the license,
 *  licenseId: string // The license id as a string
 *  product: string | undefined // The product name
 *  siteName: string | undefined // The site name
 * }[]} - A sorted list of filtered licenses with grades
 */
const getLicensesWithGrades = (
  activeDigitalLicenses,
  userAccess,
  filterType,
) => {
  if (isEmpty(activeDigitalLicenses)) {
    return []
  }
  if (!userAccess || isEmpty(userAccess)) {
    return []
  }
  if (!['K5P', 'MSP'].includes(filterType)) {
    console.error(`Unexpected filter type: ${filterType}`)
    return []
  }

  const clonedActiveDigitalLicenses = cloneDeep(activeDigitalLicenses)
  const licensesWithGrades = clonedActiveDigitalLicenses
    .map(license => {
      const { licenseId, programCodes, type: skuCode } = license

      // Filter out any licenses without a license id (likely to only happen with test accounts)
      if (!licenseId || typeof licenseId !== 'number') {
        console.error('Unexpected license shape - missing or malformed id')
        return
      }
      if (!programCodes || programCodes.length === 0) {
        console.error('Unexpected license shape - missing programCodes')
        return
      }
      if (!skuCode) {
        console.error('Unexpected license shape - missing skuCode')
        return
      }
      // filter out family licenses
      if (skuCode.endsWith('F')) {
        return
      }

      const isLicenseMs =
        filterType === 'MSP' && programCodes.includes(filterType)
      const isLicenseElem =
        filterType === 'K5P' && programCodes.includes(filterType)

      if (isLicenseMs) {
        const { mspDigital: mspDigitalAccess } = userAccess || {}
        return buildLicenseWithGrades(mspDigitalAccess, license)
      }
      if (isLicenseElem) {
        const { k5SelDigital: elemDigitalAccess } = userAccess || {}
        return buildLicenseWithGrades(elemDigitalAccess, license)
      }
    })
    .filter(x => x)
    .sort((licenseA, licenseB) =>
      licenseA.siteName > licenseB.siteName ? 1 : -1,
    )

  // Deduplicate licenses by siteName + siteId, merging grades from all licenses for the same site
  // Only deduplicate when both siteName and siteId match to avoid false positives
  const licensesBySite = new Map()
  const licensesWithoutSiteKey = [] // Licenses without siteName or siteId

  for (const license of licensesWithGrades) {
    if (!license) continue

    // Only deduplicate if we have both siteName and siteId
    // This ensures we're truly deduplicating the same site, not just same name
    // Check if siteId property exists and is not null/undefined
    // Use 'in' operator to check property existence, as siteId might be 0 (valid)
    const hasSiteId = 'siteId' in license && license.siteId != null
    if (!license.siteName || !hasSiteId) {
      licensesWithoutSiteKey.push(license)
      continue
    }

    // Create composite key: siteName + siteId
    // This ensures licenses with same siteName but different siteIds are kept separate
    const siteKey = `${license.siteName}::${license.siteId}`

    const existingLicense = licensesBySite.get(siteKey)

    if (!existingLicense) {
      licensesBySite.set(siteKey, { ...license })
    } else {
      // Merge grades from all licenses for the same site
      const existingGrades = Array.isArray(existingLicense.grades)
        ? existingLicense.grades
        : []
      const newGrades = Array.isArray(license.grades) ? license.grades : []
      const mergedGrades = [
        ...new Set([...existingGrades, ...newGrades]),
      ].sort()

      // Keep the license with more grades (prefer the one with merged grades)
      const existingGradesCount = existingGrades.length
      const newGradesCount = newGrades.length

      if (newGradesCount > existingGradesCount) {
        // Replace with the new license but use merged grades
        licensesBySite.set(siteKey, {
          ...license,
          grades: mergedGrades,
        })
      } else {
        // Update existing license with merged grades (create new object to avoid mutation)
        licensesBySite.set(siteKey, {
          ...existingLicense,
          grades: mergedGrades,
        })
      }
    }
  }

  // Combine deduplicated licenses with licenses without site key, then sort
  const deduplicatedLicenses = [
    ...Array.from(licensesBySite.values()),
    ...licensesWithoutSiteKey,
  ].sort((licenseA, licenseB) => {
    // Sort by siteName, with undefined/null siteNames at the end
    const nameA = licenseA.siteName || ''
    const nameB = licenseB.siteName || ''
    return nameA > nameB ? 1 : -1
  })

  return deduplicatedLicenses
}

const buildLicenseWithGrades = (gradeAccess, license) => {
  const { product, siteName, licenseId } = license
  const licenseIdString = licenseId.toString()
  const grades = getAccessibleGrades(gradeAccess, licenseIdString)
  const licenseObj = {
    ...license,
    grades,
    licenseId: licenseIdString,
    product: product || undefined,
    siteName: siteName || undefined,
  }
  return licenseObj
}

const getAccessibleGrades = (gradesAccess, licenseId) => {
  if (!gradesAccess || !licenseId) {
    return []
  }

  const gradeNumbers = Object.keys(gradesAccess)
  const accessibleGrades =
    gradeNumbers.filter(gradeNumber => {
      const canAccess = canAccessGrade(gradesAccess, gradeNumber, licenseId)

      if (canAccess) {
        return gradeNumber
      }
    }) || []

  return accessibleGrades
}

// if you have a teacher or an admin claim you can access the grade:
const canAccessGrade = (gradesAccess = [], gradeNumber, licenseId) => {
  const gradeAccess = gradesAccess[gradeNumber]
  const { admin, teacher } = gradeAccess || {}
  const { hasRole: hasRoleAdmin = false, licenseIds: licenseIdsAdmin } =
    admin || {}
  const { hasRole: hasRoleTeacher = false, licenseIds: licenseIdsTeacher } =
    teacher || {}

  const adminIds = Array.isArray(licenseIdsAdmin)
    ? licenseIdsAdmin
    : licenseIdsAdmin
    ? Object.values(licenseIdsAdmin)
    : []
  const teacherIds = Array.isArray(licenseIdsTeacher)
    ? licenseIdsTeacher
    : licenseIdsTeacher
    ? Object.values(licenseIdsTeacher)
    : []

  const licenseHasAccess =
    adminIds.includes(licenseId) || teacherIds.includes(licenseId)

  const canAccess =
    (hasRoleAdmin && licenseHasAccess) || (hasRoleTeacher && licenseHasAccess)

  return canAccess
}

const findAdminLicenseIds = accessObj => {
  const filterKeys = ['k5SelDigital', 'mspDigital', 'highSchool', 'sela']
  const adminLicenseIds = filterKeys
    .map(key => {
      if (!accessObj[key]) return

      const product = accessObj[key]
      const licenseIds = Object.values(product)
        .map(({ admin, family }) => {
          if (key !== 'highSchool' && admin?.hasRole) {
            return admin.licenseIds
          } else if (
            key === 'highSchool' &&
            (admin?.hasRole || family?.hasRole)
          ) {
            return [...(family?.licenseIds || []), ...(admin?.licenseIds || [])]
          }
        })
        .filter(item => item)
        .flat()

      return licenseIds
    })
    .filter(item => item)
    .flat()

  return [...new Set(adminLicenseIds)]
}

/**
 * @deprecated To be removed via LEARN-16518
 */
const filterAdminK8Licenses = (accessObj, activeDigitalLicenses) => {
  if (activeDigitalLicenses?.length === 0) return null
  const k8Types = ['K8P', 'MSP']
  const activeAdminLicenses = filterAdminLicenses(
    accessObj,
    activeDigitalLicenses,
  )
  const activeAdminK8Licenses = activeAdminLicenses?.filter(l =>
    k8Types.includes(l.type),
  )
  return activeAdminK8Licenses
}

/**
 * @deprecated To be removed via LEARN-16518
 */
const filterAdminLicenses = (accessObj, activeDigitalLicenses) => {
  if (activeDigitalLicenses?.length === 0) return null
  const adminLicenseIds = findAdminLicenseIds(accessObj)
  const filteredLicenses = activeDigitalLicenses
    ?.filter(({ licenseId }) =>
      adminLicenseIds.find(
        adminLicenseId => adminLicenseId === licenseId?.toString(),
      ),
    )
    .sort((a, b) => a['siteName']?.localeCompare(b['siteName']))

  return filteredLicenses
}

// This maps v3 licenses to v2 for pages that are managed by long-lived feature flags that we couldn't put behind v3 feature flags
const selectActiveDigitalLicenses = createSelector(
  [
    state => state.licenseManager.activeDigitalLicenses,
    state => state.licenseManager.sites,
    state => state.licenseManager.isFetchingActiveDigitalLicenses,
    state => state.licenseManager.isFetchingUserSites,
    state => state.licenseManager.error,
    state => state.ssapiManager?.sites,
    state => state.ssapiManager?.netSuiteAssets,
  ],
  (
    v3ActiveLicenses,
    v3Sites,
    isFetchingLicenses,
    isFetchingSites,
    error,
    v4SitesRaw,
    v4AssetsMap,
  ) => {
    // Prefer v4 when routed and available
    const useV4Sites = isV4Routed('getUserSites') && Array.isArray(v4SitesRaw)
    const useV4Assets =
      isV4Routed('getNetSuiteAssetsForUser') &&
      v4AssetsMap &&
      typeof v4AssetsMap === 'object'

    const userSites = useV4Sites
      ? adaptSitesV4ToLegacySites(v4SitesRaw)
      : v3Sites

    let activeLicenses = v3ActiveLicenses

    // TODO: Move to the adapter in ss api folder
    if (useV4Assets || useV4Sites) {
      const fromSites = useV4Sites
        ? (userSites || []).flatMap(site =>
            Array.isArray(site.licenses) ? site.licenses : [],
          )
        : []

      activeLicenses = fromSites
    }

    if (!activeLicenses?.length) return activeLicenses

    const shouldNotFetch = !!error || isFetchingSites || isFetchingLicenses
    const isNotHydrated = !userSites || !activeLicenses
    if (shouldNotFetch) {
      return {} // this return value can be checked in components that use this selector to prevent refetching if error or fetching in progress
    }
    if (isNotHydrated) {
      return null
    }

    const activeDigitalLicenses = activeLicenses?.map(
      ({ id, product, site, expirationDate }) => {
        // claims may be empty if the site is not returned by /sites/me
        const { claims = [] } =
          userSites?.find(({ id }) => id === site.id) || {}
        return {
          claims,
          licenseId: id,
          product: product.description,
          productType: product.type || 'BULK',
          programCodes: product.programCodes,
          siteId: site.id,
          siteName: site.name,
          type: product.skuCode,
          expirationDate,
          sku: product.sku?.skuValue,
        }
      },
    )
    return activeDigitalLicenses
  },
)

const selectActiveSites = createSelector(
  [
    ssapiSelectors.selectSitesFromSsapi,
    state => state.licenseManager?.sites,
    state => state.licenseManager.isFetchingUserSites,
    state => state.licenseManager.error,
    state => state.ssapiManager?.isFetchingUserSites,
    state => state.ssapiManager?.sites,
  ],
  (
    ssapiSites,
    legacySites,
    isFetchingUserSites,
    error,
    isFetchingSsapiSites,
    v4SitesRaw,
  ) => {
    const shouldNotFetch =
      !!error || isFetchingUserSites || isFetchingSsapiSites
    // Prefer sites from ssapiManager through translation layer, fallback to legacy
    // When ssapiSites is null (due to fetching), check if raw v4 sites exist and adapt them
    let sites = ssapiSites !== null ? ssapiSites : legacySites
    if (sites === null && Array.isArray(v4SitesRaw)) {
      sites = adaptSitesV4ToLegacySites(v4SitesRaw)
    }
    const isNotHydrated = !sites

    // Even if fetching, prefer returning the best-known active sites array (avoid {}) to keep consumers rendering
    if (shouldNotFetch && !isNotHydrated) {
      const activeDuringFetch = (sites || [])
        .filter(site => site?.licenses?.some(({ isExpired }) => !isExpired))
        .sort((a, b) => a['name']?.localeCompare(b['name']))
      return activeDuringFetch
    }
    if (isNotHydrated) {
      return null
    }

    const activeSites = (sites || [])
      .filter(site => site?.licenses?.some(({ isExpired }) => !isExpired))
      .sort((a, b) => a['name']?.localeCompare(b['name']))
    return activeSites
  },
)

const selectActiveAdminSites = createSelector(
  [selectActiveSites, state => state.userAccessManager?.access],
  (activeSites, userAccess) => {
    const isNotSettled = !Array.isArray(activeSites) || !userAccess
    if (isNotSettled) {
      return activeSites
    }

    const accessibleAdminLicenseIds = findAdminLicenseIds(userAccess)

    const activeAdminSites = activeSites.filter(site =>
      site?.licenses.some(({ id }) =>
        accessibleAdminLicenseIds.includes(id.toString()),
      ),
    )

    return activeAdminSites
  },
)

const selectActiveAdminHsSites = createSelector(
  [selectActiveAdminSites],
  activeAdminSites => {
    const isNotSettled = !Array.isArray(activeAdminSites)
    if (isNotSettled) {
      return activeAdminSites
    }
    const hsTypes = ['HSP', 'K12P', 'HSPF']

    const hsSites = activeAdminSites.filter(site =>
      site?.licenses?.some(
        ({ isExpired, product }) =>
          !isExpired &&
          product?.programCodes?.some(code => hsTypes.includes(code)),
      ),
    )
    return hsSites
  },
)

const selectActiveAdminK8Sites = createSelector(
  [selectActiveAdminSites],
  activeAdminSites => {
    const isNotSettled = !Array.isArray(activeAdminSites)
    if (isNotSettled) {
      return activeAdminSites
    }
    const k8Types = ['K5P', 'K8P', 'MSP']
    const k8Sites = activeAdminSites.filter(site =>
      site?.licenses?.some(({ isExpired, product }) => {
        return (
          product?.skuCode !== 'K8PF' &&
          !isExpired &&
          product?.programCodes?.some(code => k8Types.includes(code))
        )
      }),
    )
    return k8Sites
  },
)

const selectActiveAdminSitePreferences = createSelector(
  [selectActiveAdminSites],
  activeSites => {
    const isNotSettled = !Array.isArray(activeSites)
    if (isNotSettled) {
      return activeSites
    }
    const sitePreferences = activeSites.reduce((map, site) => {
      map[site.id] = site.sitePreferences
      return map
    }, {})
    return sitePreferences
  },
)

export default {
  filterAdminK8Licenses,
  filterAdminLicenses,
  findAdminLicenseIds,
  getLicensesWithGrades,
  selectActiveAdminHsSites,
  selectActiveAdminK8Sites,
  selectActiveAdminSites,
  selectActiveDigitalLicenses,
  selectActiveSites,
  selectActiveAdminSitePreferences,
}
